<script setup>
  import { storeToRefs } from 'pinia'
  import MsnNews from './MsnNews.vue'
  import { useNewsToggleStore } from '~/store/NewsToggle'

  const props = defineProps({
    withNewscatcher: {
      type: Boolean,
      default: false,
    },
    withProgrammatic: {
      type: Boolean,
      default: false,
    },
    enhancedHeaderDesign: {
      type: Boolean,
      default: false,
    },
  })

  const newsToggleStore = useNewsToggleStore()
  const { newsToggle } = storeToRefs(newsToggleStore)

  const getCategoriesDefaultValues = () => {
    if (props.withNewscatcher) {
      return [true, false, false, false, false, false, false, false, false, false, false, false, false, false]
    } else {
      return [true, false, false, false, false, false, false, false, false]
    }
  }

  const selectedCategories = useCookie('selectedCategories', {
    default: () => ref(getCategoriesDefaultValues()),
    watch: true,
  })

  const categories = props.withNewscatcher
    ? [
        'All',
        'News',
        'Sports',
        'Politics',
        'Entertainment',
        'Money',
        'Lifestyle',
        'Technology',
        'Food',
        'Travel',
        'Health',
        'Automotive',
        'Shopping',
        'Weather',
      ]
    : ['All', 'News', 'Sports', 'Money', 'Lifestyle', 'Health', 'Shopping', 'Travel', 'Weather']

  const currentIndex = selectedCategories.value.indexOf(true)

  const getSelectedCategory = () => {
    if (currentIndex !== -1) {
      return categories[currentIndex]
    } else if (route.name === 'v4') {
      return categories[1]
    } else {
      return categories[0]
    }
  }

  const selectedCategory = useCookie('selectedCategory', {
    default: () => ref(getSelectedCategory()),
    watch: true,
  })

  const navBarWidth = computed({
    get() {
      if (process.client) {
        return window.innerWidth >= 1920 ? 1764 + 'px' : 1272 + 'px'
      }
    },
  })

  const navBarPaddingLeft = computed({
    get() {
      if (process.client) {
        return window.innerWidth >= 1920 ? 84 + 'px' : 0
      }
    },
  })

  const isFiveTiles = computed({
    get() {
      return window.innerWidth >= 1920
    },
  })

  const fixedTopMargin = props.enhancedHeaderDesign ? ref('95px') : ref('76px')
  const fixedTopMarginSmallScreen = props.enhancedHeaderDesign ? ref('95px') : ref('63px')

  let navMenu
  let threeDotMenu
  let verticalTab

  // function to check if the navigation menu fits on the screen
  function checkNavMenu() {
    // both following if conditions are used to adjust navigation bar heading margin for new header design
    if (
      props.enhancedHeaderDesign &&
      ((window.innerWidth <= 772 && isFiveTiles.value) || (window.innerWidth <= 1034 && !isFiveTiles.value))
    ) {
      fixedTopMargin.value = '139px'
      fixedTopMarginSmallScreen.value = '139px'
    }

    if (
      props.enhancedHeaderDesign &&
      ((window.innerWidth > 772 && isFiveTiles.value) || (window.innerWidth > 1034 && !isFiveTiles.value))
    ) {
      fixedTopMargin.value = '95px'
      fixedTopMarginSmallScreen.value = '95px'
    }

    if (!threeDotMenu) {
      return
    }

    // get the width of the navigation menu and the screen
    const navMenuWidth = navMenu.offsetWidth

    // check if the navigation menu fits on the screen
    const children = navMenu.children
    if (navMenuWidth < 100 * navMenu.childElementCount - 44) {
      threeDotMenu.style.display = 'flex'

      const childrenToAppend = categories.length - Math.ceil(navMenuWidth / 103) - verticalTab.children.length
      const arr = [...children]
      const b = arr.slice(children.length - childrenToAppend)

      for (let i = b.length - 1; i >= 0; i--) {
        verticalTab.prepend(b[i])
      }
    } else if (verticalTab.children.length > 0) {
      const childrenToRemove =
        verticalTab.children.length - (categories.length - Math.ceil(navMenuWidth / 103))
      const a = [...verticalTab.children]

      for (let i = 0; i < childrenToRemove; i++) {
        if (a[i] && a[i]?.tagName != null) {
          verticalTab.removeChild(a[i])
          navMenu.appendChild(a[i])
        }
      }
    } else {
      threeDotMenu.style.display = 'none'
      verticalTab.style.display = 'none'
    }
  }

  onBeforeMount(() => {
    const ele = document.getElementById('logo')

    if (ele) {
      ele.className += window.innerWidth >= 1920 ? 'logo-default-1920' : 'logo-default'
    }
  })

  onMounted(() => {
    if (newsToggle.value) {
      // run the checkNavMenu function on page load and when the window is resized
      navMenu = document.querySelector('#nav-list')
      threeDotMenu = document.querySelector('#three-dot-menu')
      verticalTab = document.querySelector('#vertical-tab')

      // add an event listener to the three-dot menu button to open the vertical tab or dropdown menu
      threeDotMenu.addEventListener('click', () => {
        // open the vertical tab or dropdown menu
        if (verticalTab.style.display === 'inline-grid') {
          verticalTab.style.display = 'none'
        } else if (verticalTab.childElementCount > 0) {
          verticalTab.style.display = 'inline-grid'
        }
      })

      document.addEventListener('click', (e) => {
        // Check if the filter list parent element exist
        const isClosest = e.target.closest('#three-dot-menu')

        if (!(isClosest || e.target === verticalTab || e.target === threeDotMenu)) {
          verticalTab.style.display = 'none'
        }
      })

      checkNavMenu()
      window.addEventListener('resize', checkNavMenu)

      const allnews = document.querySelector('#all')
      const news = document.querySelector('#news')
      const sports = document.querySelector('#sports')
      const money = document.querySelector('#money')
      const lifestyle = document.querySelector('#lifestyle')
      const health = document.querySelector('#health')
      const shopping = document.querySelector('#shopping')
      const travel = document.querySelector('#travel')

      let politics = null
      let entertainment = null
      let technology = null
      let food = null
      let automotive = null

      if (props.withNewscatcher) {
        politics = document.querySelector('#politics')
        entertainment = document.querySelector('#entertainment')
        technology = document.querySelector('#technology')
        food = document.querySelector('#food')
        automotive = document.querySelector('#automotive')
      }

      const elementList = props.withNewscatcher
        ? [
            allnews,
            news,
            sports,
            politics,
            entertainment,
            money,
            lifestyle,
            technology,
            food,
            travel,
            health,
            automotive,
            shopping,
          ]
        : [allnews, news, sports, money, lifestyle, health, shopping, travel]

      for (let i = 1; i < selectedCategories.value.length; i++) {
        if (selectedCategories.value[i]) {
          elementList[i].classList.add('active-button')
          elementList[0].classList.remove('active-button')
        }
      }
    }
  })

  function changeCategory(index) {
    if (selectedCategories.value.filter(Boolean).length === 0) {
      index = 0
      selectedCategory.value = categories.at(index)
    }
    if (selectedCategories.value[index] === true) {
      index = 0
    }
    selectedCategories.value = Array(selectedCategories.value.length).fill(false)
    selectedCategories.value[index] = !selectedCategories.value[index]
    selectedCategory.value = categories.at(index)
  }

  function openUrl() {
    if (process.client) {
      window
        .open(
          'https://www.accuweather.com/en/ca/woburn/m1h/current-weather/3394003?lang=en-us&partner=web_onelaunch_ntp_adc',
          '_blank'
        )
        .focus()
    }
  }
</script>

<template>
  <div v-if="newsToggle">
    <div id="navigation" class="navigation">
      <div
        class="navigation__links"
        :class="{
          'small-layout': !isFiveTiles,
          'big-layout': isFiveTiles,
        }"
      >
        <ul id="nav-list">
          <li v-for="(category, index) in categories" :id="category.toLowerCase() + '-li'" :key="index">
            <button
              :id="category.toLowerCase()"
              :class="['button', selectedCategories[index] ? 'active-button disable' : '']"
              @click="index === categories.length - 1 ? openUrl() : changeCategory(index)"
            >
              <span class="button-text">{{ $t(`category.${category.toLowerCase()}`) }}</span>
            </button>
          </li>
        </ul>
        <div class="navigation__menu">
          <!-- three-dot menu button -->
          <div id="three-dot-menu" class="navigation__three-dot-menu">
            <span class="bg-three_dots_menu dark:bg-radio_group_text_color_dark" />
            <span class="bg-three_dots_menu dark:bg-radio_group_text_color_dark" />
            <span class="bg-three_dots_menu dark:bg-radio_group_text_color_dark" />
          </div>
          <!-- vertical tab -->
          <ul
            id="vertical-tab"
            class="navigation__vertical-tab border-[1px] border-solid border-vertical_tab_shadow bg-onelaunch_ui_shades_50 dark:border-border_header_summary dark:bg-radio_group_text_selected_dark"
          />
        </div>
      </div>
    </div>

    <MsnNews
      :category="selectedCategories"
      :selected-category="selectedCategory"
      :with-newscatcher="withNewscatcher"
      :with-programmatic="withProgrammatic"
    />
  </div>
</template>

<style lang="scss" scoped>
  .navigation {
    --button-color: #464c52;
    --button-selected-color: #005eb7;
    --button-hover-color: #d9d9d9;
    --border-color: #eaecee;
    --button-text-hover-color: black;
    --background-color: #fff;

    display: flex;
    height: 40px;
    border-top: 1px solid var(--border-color);
    border-bottom: 1px solid var(--border-color);

    &__links {
      height: 40px;
      display: flex;
      padding: 0;
      width: v-bind(navBarWidth);
      margin-left: auto;
      margin-right: auto;
      padding-left: v-bind(navBarPaddingLeft);
    }

    ul#nav-list {
      display: flex;
      list-style-type: none;
      padding: 8px 8px 8px 0;
      text-align: initial;
      width: inherit;
      overflow: hidden;
      flex-wrap: wrap;
      margin: 0;
    }

    li {
      display: inline;
      margin: 0 20px;
      font-size: 14px;
      font-weight: 500;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.71;
      letter-spacing: normal;
      text-align: left;
      color: var(--button-color);

      .button {
        background: none;
        color: inherit;
        border: none;
        padding: 0;
        outline: inherit;
        font: inherit;
        cursor: pointer;
      }

      .button:not(.disable):hover {
        color: var(--button-hover-color);
        text-decoration: underline;
        text-underline-offset: 8.9px;
        text-underline-position: under;
        text-decoration-thickness: 2px;

        .button-text {
          color: var(--button-text-hover-color);
        }
      }

      .active-button {
        color: var(--button-selected-color);
        text-decoration: underline;
        text-underline-offset: 8.9px;
        text-underline-position: under;
        text-decoration-thickness: 2px;
      }
    }

    li#all-li {
      margin: 0 20px 0 0;
    }

    &__three-dot-menu {
      display: none;
      cursor: pointer;
      width: fit-content;
      margin-top: 16px;

      span {
        width: 4px;
        height: 4px;
        margin: 2px;
        border-radius: 50%;
      }
    }

    /* style the vertical tab */

    &__vertical-tab {
      position: absolute;
      z-index: 1;
      display: none;
      min-width: 160px;
      padding: 12px 16px;
      margin: 14px 0 0 -96px;

      li {
        display: block;
        height: 38px;
      }
    }

    @media (max-width: 360px) {
      &__vertical-tab {
        padding: 12px 10px;

        li {
          margin: 0 5px;
        }
      }
    }

    &--fixed {
      z-index: 99;
      top: v-bind(fixedTopMargin);
      position: fixed;
      background-color: var(--background-color);
      width: 100%;

      @media (max-width: 1400px) {
        top: v-bind(fixedTopMarginSmallScreen);
      }
    }
  }

  .small-layout {
    @media (max-width: 1360px) {
      width: 948px;
      padding-left: 0;
    }

    @media (max-width: 1034px) {
      width: 624px;
      padding-left: 0;
    }

    @media (max-width: 772px) {
      width: 300px;
      padding-left: 0;
    }
  }

  .big-layout {
    @media (max-width: 1860px) {
      width: 1272px;
      padding-left: 0;
    }

    @media (max-width: 1506px) {
      width: 948px;
      padding-left: 0;
    }

    @media (max-width: 1145px) {
      width: 624px;
      padding-left: 0;
    }

    @media (max-width: 772px) {
      width: 300px;
      padding-left: 0;
    }
  }

  // Dark theme
  .dark .navigation {
    --button-color: #9d9ea1;
    --button-selected-color: #98ecfe;
    --button-hover-color: #8a8a8e;
    --border-color: #9d9ea11a;
    --button-text-hover-color: #8a8a8e;
    --background-color: #26272e;
  }
</style>
